import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  ButtonGroup,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Grid,
  Paper,
  Divider,
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Tabs,
  Tab
} from '@mui/material';
import { useTheme, makeStyles } from '@mui/styles';
import { getChipVariant } from '../utils/chipStyles';
import { getButtonGroupStyles } from '../utils/buttonGroupStyles';
import SuccessChip from '../elements/Chips/SuccessChip';
import FailureChip from '../elements/Chips/FailureChip';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  FlashOn as LightningIcon
} from '@mui/icons-material';

/**
 * Theme Showcase Component
 * Demonstrates the new APL Ubil+ theme design system
 * This component can be used for testing and documentation purposes
 */
const ThemeShowcase = () => {
  const theme = useTheme();
  const [buttonGroupValue, setButtonGroupValue] = React.useState('option1');
  const [toggleValue, setToggleValue] = React.useState('left');
  const [tabValue, setTabValue] = React.useState(0);

  const useStyles = makeStyles((theme) => getButtonGroupStyles(theme));
  const classes = useStyles();

  const chipTypes = ['success', 'warning', 'error', 'info', 'active', 'inactive', 'tenant', 'alert', 'default'];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h3" gutterBottom color="primary">
        APL Ubil+ Theme Showcase
      </Typography>
      <Typography variant="subtitle1" color="textSecondary" gutterBottom>
        Demonstrating the new Deep Navy & Orange design system
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* Color Palette */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom>
            Color Palette
          </Typography>
        </Grid>

        {/* Primary Colors */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Primary Colors (Deep Navy)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Paper 
                  sx={{ 
                    width: 60, 
                    height: 60, 
                    backgroundColor: theme.palette.primary.light,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '10px'
                  }}
                >
                  Light
                </Paper>
                <Paper 
                  sx={{ 
                    width: 60, 
                    height: 60, 
                    backgroundColor: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '10px'
                  }}
                >
                  Main
                </Paper>
                <Paper 
                  sx={{ 
                    width: 60, 
                    height: 60, 
                    backgroundColor: theme.palette.primary.dark,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '10px'
                  }}
                >
                  Dark
                </Paper>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Professional, trustworthy, stable
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Secondary Colors */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Secondary Colors (Amber)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Paper 
                  sx={{ 
                    width: 60, 
                    height: 60, 
                    backgroundColor: theme.palette.secondary.light,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'black',
                    fontSize: '10px'
                  }}
                >
                  Light
                </Paper>
                <Paper 
                  sx={{ 
                    width: 60, 
                    height: 60, 
                    backgroundColor: theme.palette.secondary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'black',
                    fontSize: '10px'
                  }}
                >
                  Main
                </Paper>
                <Paper 
                  sx={{ 
                    width: 60, 
                    height: 60, 
                    backgroundColor: theme.palette.secondary.dark,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'black',
                    fontSize: '10px'
                  }}
                >
                  Dark
                </Paper>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Warm, professional, call-to-action
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Buttons */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Button Examples
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button variant="contained" color="primary">
                  Primary Action
                </Button>
                <Button variant="contained" color="secondary">
                  Secondary Action
                </Button>
                <Button variant="outlined" color="primary">
                  Outlined Primary
                </Button>
                <Button variant="outlined" color="secondary">
                  Outlined Secondary
                </Button>
                <Button variant="text" color="primary">
                  Text Primary
                </Button>
                <Button variant="contained" color="success">
                  Success
                </Button>
                <Button variant="contained" color="warning">
                  Warning
                </Button>
                <Button variant="contained" color="error">
                  Error
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Status Chips */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Status Chips (Using Design System)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {chipTypes.map((type) => {
                  const chipProps = getChipVariant(type, theme);
                  return (
                    <Chip
                      key={type}
                      label={type.charAt(0).toUpperCase() + type.slice(1)}
                      {...chipProps}
                    />
                  );
                })}
              </Box>
              <Typography variant="body2" color="textSecondary">
                All chips follow consistent design system patterns
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* API Key Status Examples */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                API Key Status Examples
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Chip label="Active" {...getChipVariant('success', theme)} size="small" />
                <Chip label="Inactive" {...getChipVariant('warning', theme)} size="small" />
                <Chip label="Revoked" {...getChipVariant('error', theme)} size="small" />
              </Box>
              <Typography variant="body2" color="textSecondary">
                API Key status chips: Active (success/green), Inactive (warning/orange), Revoked (error/red)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* ButtonGroup Examples */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                ButtonGroup Components
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <ButtonGroup variant="outlined" color="primary">
                  <Button 
                    className={buttonGroupValue === 'option1' ? classes.active : classes.inactive}
                    onClick={() => setButtonGroupValue('option1')}
                  >
                    Option 1
                  </Button>
                  <Button 
                    className={buttonGroupValue === 'option2' ? classes.active : classes.inactive}
                    onClick={() => setButtonGroupValue('option2')}
                  >
                    Option 2
                  </Button>
                  <Button 
                    className={buttonGroupValue === 'option3' ? classes.active : classes.inactive}
                    onClick={() => setButtonGroupValue('option3')}
                  >
                    Option 3
                  </Button>
                </ButtonGroup>
              </Box>
              <Typography variant="body2" color="textSecondary">
                ButtonGroups with consistent active/inactive styling
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* ToggleButtonGroup Examples */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                ToggleButtonGroup Components
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <ToggleButtonGroup
                  value={toggleValue}
                  exclusive
                  onChange={(event, newValue) => setToggleValue(newValue)}
                >
                  <ToggleButton value="left">Left</ToggleButton>
                  <ToggleButton value="center">Center</ToggleButton>
                  <ToggleButton value="right">Right</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Typography variant="body2" color="textSecondary">
                ToggleButtonGroups with theme-consistent styling
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* AppBar Examples */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                AppBar Components
              </Typography>
              <Box sx={{ mb: 2 }}>
                <AppBar position="static" elevation={2}>
                  <Toolbar>
                    <LightningIcon sx={{ mr: 2, fontSize: 28 }} />
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                      AP&L UBIL+
                    </Typography>
                    <IconButton size="large" color="inherit">
                      <SearchIcon />
                    </IconButton>
                    <IconButton size="large" color="inherit">
                      <Badge badgeContent={4} color="secondary">
                        <NotificationsIcon />
                      </Badge>
                    </IconButton>
                    <IconButton size="large" color="inherit">
                      <AccountCircleIcon />
                    </IconButton>
                  </Toolbar>
                </AppBar>
              </Box>
              <Typography variant="body2" color="textSecondary">
                AppBar with Deep Navy background and proper contrast
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* AppBar with Tabs Examples */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                AppBar with Tabs
              </Typography>
              <Box sx={{ mb: 2 }}>
                <AppBar position="static" color="primary">
                  <Tabs 
                    value={tabValue} 
                    onChange={(event, newValue) => setTabValue(newValue)}
                  >
                    <Tab label="Settings" />
                    <Tab label="Users" />
                    <Tab label="Profile" />
                    <Tab label="Reports" />
                  </Tabs>
                </AppBar>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Tabs with white text on Deep Navy background and Orange indicator
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Legacy Chip Components */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Updated Legacy Components
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <SuccessChip text="Success Chip" />
                <FailureChip text="Failure Chip" />
              </Box>
              <Typography variant="body2" color="textSecondary">
                Legacy components now use the new theme system
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Semantic Colors */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Semantic Color Usage
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ p: 2, backgroundColor: theme.palette.success.light, borderRadius: 1 }}>
                    <Typography variant="body2" color={theme.palette.success.dark}>
                      Success State
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ p: 2, backgroundColor: theme.palette.warning.light, borderRadius: 1 }}>
                    <Typography variant="body2" color={theme.palette.warning.dark}>
                      Warning State
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ p: 2, backgroundColor: theme.palette.error.light, borderRadius: 1 }}>
                    <Typography variant="body2" color={theme.palette.error.dark}>
                      Error State
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ p: 2, backgroundColor: theme.palette.info.light, borderRadius: 1 }}>
                    <Typography variant="body2" color={theme.palette.info.dark}>
                      Info State
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, p: 2, backgroundColor: theme.palette.grey[100], borderRadius: 1 }}>
        <Typography variant="body2" color="textSecondary">
          <strong>Theme Implementation Complete:</strong> The APL Ubil+ application now uses a cohesive 
          design system with Deep Navy (#003366) as the primary color and Orange (#d67104) as the secondary color. 
          All hardcoded colors have been replaced with theme variables, and chip components follow consistent patterns.
        </Typography>
      </Box>
    </Box>
  );
};

export default ThemeShowcase;
